import { useCallback, useEffect, useState } from 'react';
import OverlayPanel from '../common/overlay-panel/overlay-panel.component';
import type { OverlayScrollDetectionType } from '../common/overlay-panel/overlay-panel.props';
import type { DomElementAlignment } from '../enums';
import { DomTargetPosition } from '../enums';
import { isDropDownNoSelection } from '../fields/drop-down/drop-down-common';
import type { DropDownNoSelection, DropDownResult } from '../fields/drop-down/drop-down-types';
import DateRangeFilterField from '../data-grid/filters/date-range-filter-field.component';
import { getCustomDateRangeItem, getDateRanges } from './date-range-filter-functions';

export interface DateRange {
  from?: Date;
  to?: Date;
}

interface DateRangeFilterProps {
  value?: DateRange;
  selectedQuickDateRange?: string;
  visible: boolean;
  onApplyFilter: (dateRange: DateRange, quickDateRange?: DropDownResult) => void;
  onClearFilter: () => void;
  onHidePanel?: () => void;
  target?: React.MouseEvent<Element, MouseEvent> | HTMLElement | Element | null;
  alignment?: DomElementAlignment;
  position?: DomTargetPosition;
  scrollDetection?: OverlayScrollDetectionType;
}

const DateRangeFilter = ({
  value,
  selectedQuickDateRange,
  visible,
  onApplyFilter,
  onClearFilter,
  onHidePanel,
  target,
  alignment,
  position = DomTargetPosition.BottomLeft,
  scrollDetection,
}: DateRangeFilterProps) => {
  const [quickDateRange, setQuickDateRange] = useState<DropDownResult | DropDownNoSelection>();
  const [fromDate, setFromDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();

  useEffect(() => {
    if (value) {
      if (!fromDate && !toDate && !quickDateRange) {
        setFromDate(value.from);
        setToDate(value.to);
        if (selectedQuickDateRange) {
          const dateRange = getDateRanges().find((range) => range.value === selectedQuickDateRange);
          if (dateRange) {
            setQuickDateRange(dateRange);
          } else {
            setQuickDateRange(getCustomDateRangeItem());
          }
        }
      }
    } else {
      clearFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only sync when value prop changes, check state to avoid overwriting user changes
  }, [value]);

  const clearFilter = () => {
    setFromDate(undefined);
    setToDate(undefined);
    setQuickDateRange(undefined);
    onClearFilter();
  };

  const handleHidden = () => {
    onHidePanel?.();
  };

  const handleApplyClick = useCallback(() => {
    if ((fromDate || toDate) && !isDropDownNoSelection(quickDateRange)) {
      onApplyFilter({ from: fromDate, to: toDate }, quickDateRange);
    }
    onHidePanel?.();
  }, [fromDate, toDate, quickDateRange, onApplyFilter, onHidePanel]);

  return (
    <OverlayPanel
      visible={visible}
      target={target}
      targetPosition={position}
      elementAlignment={alignment}
      scrollDetection={scrollDetection}
      hidden={handleHidden}
      shouldScrollCloseOverlay
      shouldRemainOnScreen
      shouldCheckZIndex
      className="odin-w-[400px] odin-min-w-min odin-max-w-lg odin-p-4 odin-mt-2 odin-bg-mono-1 odin-shadow-lg odin-border odin-border-gray-4 odin-rounded-md odin-flex odin-gap-y-4 odin-flex-col"
    >
      <DateRangeFilterField
        fromDate={fromDate}
        toDate={toDate}
        quickDateRange={quickDateRange}
        setFromDate={setFromDate}
        setToDate={setToDate}
        setQuickDateRange={setQuickDateRange}
        clearFilter={clearFilter}
        showApplyButton
        onApplyClick={handleApplyClick}
      />
    </OverlayPanel>
  );
};

export default DateRangeFilter;
